import React, { useState, useEffect } from 'react'
import { Calendar, User, Lock, Mail, ArrowLeft, CheckCircle } from 'lucide-react'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

const LoginScreen = ({ onLogin }) => {
  const [view, setView] = useState('login') // 'login' | 'forgot' | 'reset' | 'resetDone'
  const [loginForm, setLoginForm] = useState({ username: '', password: '' })
  const [loginError, setLoginError] = useState('')

  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotStatus, setForgotStatus] = useState(null) // null | 'loading' | 'sent' | 'error'

  const [resetToken, setResetToken] = useState(null)
  const [resetForm, setResetForm] = useState({ password: '', confirm: '' })
  const [resetError, setResetError] = useState('')
  const [resetLoading, setResetLoading] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('reset')
    if (token) {
      setResetToken(token)
      setView('reset')
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  const handleSubmit = () => {
    setLoginError('')
    const result = onLogin(loginForm.username, loginForm.password)

    if (!result.success) {
      setLoginError('Το όνομα χρήστη ή/και ο κωδικός είναι λανθασμένα.')
    } else if (result.success) {
      setLoginForm({ username: '', password: '' })
    }
  }

  const handleForgotSubmit = async () => {
    if (!forgotEmail.trim()) return
    setForgotStatus('loading')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() })
      })
      const data = await response.json()
      setForgotStatus(data.success ? 'sent' : 'error')
    } catch {
      setForgotStatus('error')
    }
  }

  const handleResetSubmit = async () => {
    setResetError('')
    if (resetForm.password.length < 6) {
      setResetError('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες.')
      return
    }
    if (resetForm.password !== resetForm.confirm) {
      setResetError('Οι κωδικοί δεν ταιριάζουν.')
      return
    }
    setResetLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword: resetForm.password })
      })
      const data = await response.json()
      if (data.success) {
        setView('resetDone')
      } else {
        setResetError(data.error || 'Σφάλμα. Παρακαλώ ζητήστε νέο σύνδεσμο.')
      }
    } catch {
      setResetError('Σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε ξανά.')
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center bg-gradient-to-b from-sky-100 to-teal-50 dark:from-jewel-bg dark:to-jewel-bg">
      <style>{`
        :root {
          --wave-1: rgba(23, 82, 198, 0.3);
          --wave-2: rgba(23, 146, 244, 0.25);
          --wave-3: rgba(64, 224, 208, 0.2);
        }
        .dark {
          --wave-1: rgba(127, 29, 29, 0.55);
          --wave-2: rgba(180, 52, 33, 0.45);
          --wave-3: rgba(120, 53, 15, 0.5);
          --moon-glow-inner: rgba(125, 211, 252, 0.4);
          --moon-glow-outer: rgba(125, 211, 252, 0);
          --moon-base-1: #f0f9ff;
          --moon-base-2: #dceefc;
          --moon-base-3: #b9d9ec;
          --moon-base-4: #92b8cf;
          --moon-shade: #7fa3bc;
          --moon-crater-1: #aecbdd;
          --moon-crater-2: #8fafc4;
          --moon-crater-3: #6e8ea3;
          --moon-highlight: #f0f9ff;
          --star-color: #bae6fd;
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 1; }
        }
        @keyframes moonSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div className="absolute inset-0 overflow-hidden z-0">
        <svg
          className="absolute bottom-0 left-0 w-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          style={{ height: '40%' }}
        >
          <path
            fill="var(--wave-1)"
            d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,133.3C672,139,768,181,864,181.3C960,181,1056,139,1152,122.7C1248,107,1344,117,1392,122.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          >
            <animate
              attributeName="d"
              dur="20s"
              repeatCount="indefinite"
              values="
                M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,133.3C672,139,768,181,864,181.3C960,181,1056,139,1152,122.7C1248,107,1344,117,1392,122.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,128C672,128,768,160,864,165.3C960,171,1056,149,1152,133.3C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,133.3C672,139,768,181,864,181.3C960,181,1056,139,1152,122.7C1248,107,1344,117,1392,122.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            />
          </path>
        </svg>
        <svg
          className="absolute bottom-0 left-0 w-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          style={{ height: '35%' }}
        >
          <path
            fill="var(--wave-2)"
            d="M0,160L48,165.3C96,171,192,181,288,170.7C384,160,480,128,576,128C672,128,768,160,864,186.7C960,213,1056,235,1152,218.7C1248,203,1344,149,1392,122.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          >
            <animate
              attributeName="d"
              dur="15s"
              repeatCount="indefinite"
              values="
                M0,160L48,165.3C96,171,192,181,288,170.7C384,160,480,128,576,128C672,128,768,160,864,186.7C960,213,1056,235,1152,218.7C1248,203,1344,149,1392,122.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                M0,192L48,181.3C96,171,192,149,288,149.3C384,149,480,171,576,186.7C672,203,768,213,864,202.7C960,192,1056,160,1152,144C1248,128,1344,128,1392,128L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                M0,160L48,165.3C96,171,192,181,288,170.7C384,160,480,128,576,128C672,128,768,160,864,186.7C960,213,1056,235,1152,218.7C1248,203,1344,149,1392,122.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            />
          </path>
        </svg>
        <svg
          className="absolute bottom-0 left-0 w-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          style={{ height: '30%' }}
        >
          <path
            fill="var(--wave-3)"
            d="M0,224L48,208C96,192,192,160,288,154.7C384,149,480,171,576,192C672,213,768,235,864,229.3C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          >
            <animate
              attributeName="d"
              dur="25s"
              repeatCount="indefinite"
              values="
                M0,224L48,208C96,192,192,160,288,154.7C384,149,480,171,576,192C672,213,768,235,864,229.3C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                M0,256L48,240C96,224,192,192,288,181.3C384,171,480,181,576,197.3C672,213,768,235,864,234.7C960,235,1056,213,1152,192C1248,171,1344,149,1392,138.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                M0,224L48,208C96,192,192,160,288,154.7C384,149,480,171,576,192C672,213,768,235,864,229.3C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            />
          </path>
        </svg>
      </div>

      {/* Night sky: moon + stars, dark mode only */}
      <div className="hidden dark:block absolute top-0 right-0 w-full h-full pointer-events-none z-0 overflow-hidden">
        <svg
          className="absolute"
          style={{ top: '6%', right: '7%', width: '180px', height: '180px' }}
          viewBox="0 0 280 280"
        >
          <defs>
            <radialGradient id="moonGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--moon-glow-inner)" />
              <stop offset="100%" stopColor="var(--moon-glow-outer)" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="moonBase" cx="38%" cy="35%" r="75%">
              <stop offset="0%" stopColor="var(--moon-base-1)" />
              <stop offset="55%" stopColor="var(--moon-base-2)" />
              <stop offset="85%" stopColor="var(--moon-base-3)" />
              <stop offset="100%" stopColor="var(--moon-base-4)" />
            </radialGradient>
            <radialGradient id="moonShade" cx="40%" cy="35%" r="65%">
              <stop offset="0%" stopColor="var(--moon-shade)" stopOpacity="0.9" />
              <stop offset="100%" stopColor="var(--moon-shade)" stopOpacity="0" />
            </radialGradient>
          </defs>

          <circle cx="140" cy="140" r="135" fill="url(#moonGlow)" />
          <g style={{ transformOrigin: '140px 140px', animation: 'moonSpin 240s linear infinite' }}>
            <circle cx="140" cy="140" r="95" fill="url(#moonBase)" />

            <ellipse cx="105" cy="95" rx="22" ry="20" fill="var(--moon-crater-1)" opacity="0.55" />
            <ellipse cx="100" cy="90" rx="14" ry="13" fill="var(--moon-crater-2)" opacity="0.5" />
            <ellipse cx="97" cy="87" rx="7" ry="6.5" fill="var(--moon-crater-3)" opacity="0.4" />
            <ellipse cx="103" cy="92" rx="9" ry="3" fill="var(--moon-highlight)" opacity="0.3" />

            <ellipse cx="165" cy="120" rx="16" ry="15" fill="var(--moon-crater-1)" opacity="0.5" />
            <ellipse cx="168" cy="118" rx="9" ry="8.5" fill="var(--moon-crater-2)" opacity="0.45" />
            <ellipse cx="170" cy="116" rx="4" ry="3.5" fill="var(--moon-crater-3)" opacity="0.4" />

            <ellipse cx="125" cy="155" rx="26" ry="23" fill="var(--moon-crater-1)" opacity="0.45" />
            <ellipse cx="120" cy="150" rx="16" ry="14" fill="var(--moon-crater-2)" opacity="0.4" />
            <ellipse cx="115" cy="146" rx="6" ry="5.5" fill="var(--moon-crater-3)" opacity="0.4" />
            <ellipse cx="124" cy="152" rx="11" ry="4" fill="var(--moon-highlight)" opacity="0.25" />

            <ellipse cx="180" cy="165" rx="13" ry="12" fill="var(--moon-crater-1)" opacity="0.5" />
            <ellipse cx="183" cy="163" rx="7" ry="6.5" fill="var(--moon-crater-2)" opacity="0.45" />

            <circle cx="145" cy="105" r="5" fill="var(--moon-crater-1)" opacity="0.5" />
            <circle cx="195" cy="135" r="4" fill="var(--moon-crater-1)" opacity="0.45" />
            <circle cx="135" cy="180" r="6" fill="var(--moon-crater-1)" opacity="0.4" />
            <circle cx="200" cy="155" r="3" fill="var(--moon-crater-1)" opacity="0.45" />
            <circle cx="160" cy="195" r="4.5" fill="var(--moon-crater-1)" opacity="0.4" />
            <circle cx="90" cy="130" r="3.5" fill="var(--moon-crater-1)" opacity="0.4" />
          </g>
          <circle cx="140" cy="140" r="95" fill="url(#moonShade)" />
          <circle cx="140" cy="140" r="95" fill="none" stroke="var(--moon-crater-3)" strokeWidth="1" opacity="0.3" />
        </svg>

        {[
          { top: '4%',  left: '68%', size: 3,   delay: '0s' },
          { top: '13%', left: '84%', size: 2,   delay: '0.6s' },
          { top: '22%', left: '62%', size: 2.5, delay: '1.2s' },
          { top: '8%',  left: '91%', size: 2,   delay: '1.8s' },
          { top: '28%', left: '76%', size: 2,   delay: '0.3s' },
          { top: '2%',  left: '56%', size: 2.5, delay: '1.5s' },
          { top: '34%', left: '88%', size: 2,   delay: '2.1s' },
        ].map((star, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              top: star.top,
              left: star.left,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: 'var(--star-color)',
              animation: `twinkle 3s ease-in-out infinite`,
              animationDelay: star.delay,
            }}
          />
        ))}
      </div>
      <div className="relative z-50 bg-white/50 dark:bg-jewel-bg/90 rounded-xl shadow-2xl w-full max-w-md p-8 border border-white/30 dark:border-jewel-border">
        {view === 'login' && (
          <>
            <div className="text-center mb-8">
              <div className="bg-teal-100 dark:bg-jewel-teal/30 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-teal-600 dark:text-jewel-teal-tint" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-jewel-text mb-2">
                Ημερολόγιο Αδειών
              </h1>
              <p className="text-gray-600 dark:text-jewel-text-muted">
                Συνδεθείτε για να διαχειριστείτε τις ημερομηνίες των αδειών σας.
              </p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSubmit()
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-jewel-text mb-2">
                  Όνομα Χρήστη
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
                  <input
                    type="text"
                    value={loginForm.username}
                    onChange={(e) =>
                      setLoginForm({ ...loginForm, username: e.target.value })
                    }
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-jewel-border rounded-lg focus:ring-2 focus:ring-teal-500 bg-white/80 dark:bg-jewel-surface text-gray-900 dark:text-jewel-text"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-jewel-text mb-2">
                  Κωδικός
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
                  <input
                    type="password"
                    value={loginForm.password}
                    onChange={(e) =>
                      setLoginForm({ ...loginForm, password: e.target.value })
                    }
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-jewel-border rounded-lg focus:ring-2 focus:ring-teal-500 bg-white/80 dark:bg-jewel-surface text-gray-900 dark:text-jewel-text"
                    autoComplete="off"
                  />
                </div>
              </div>
              {loginError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {loginError}
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-calm-green-gradient dark:bg-none dark:bg-jewel-forest text-black dark:text-jewel-text font-semibold py-2 px-4 rounded-lg transition hover:opacity-90"
              >
                Σύνδεση
              </button>
              <button
                type="button"
                onClick={() => { setView('forgot'); setForgotStatus(null); setForgotEmail('') }}
                className="w-full text-sm text-teal-700 dark:text-jewel-teal-tint hover:text-teal-900 dark:hover:text-jewel-text hover:underline transition-colors pt-1"
              >
                Ξέχασα τον κωδικό μου
              </button>
            </form>
          </>
        )}

        {view === 'forgot' && (
          <>
            <button
              onClick={() => setView('login')}
              className="flex items-center gap-1 text-sm text-gray-500 dark:text-jewel-text-muted hover:text-gray-700 dark:hover:text-jewel-text mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Επιστροφή στη σύνδεση
            </button>
            <div className="text-center mb-8">
              <div className="bg-sky-100 dark:bg-jewel-teal/30 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-sky-600 dark:text-jewel-teal-tint" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-jewel-text mb-2">Επαναφορά Κωδικού</h1>
              <p className="text-gray-600 dark:text-jewel-text-muted text-sm">
                Εισάγετε το email σας και θα σας αποστείλουμε σύνδεσμο για επαναφορά κωδικού.
              </p>
            </div>

            {forgotStatus === 'sent' ? (
              <div className="text-center space-y-4">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                <p className="text-gray-700 dark:text-jewel-text font-medium">
                  Αν το email υπάρχει στο σύστημα, θα λάβετε σύντομα οδηγίες επαναφοράς.
                </p>
                <p className="text-gray-500 dark:text-jewel-text-muted text-sm">Ελέγξτε και τον φάκελο spam.</p>
                <button
                  onClick={() => setView('login')}
                  className="w-full bg-calm-green-gradient dark:bg-none dark:bg-jewel-forest text-black dark:text-jewel-text font-semibold py-2 px-4 rounded-lg transition hover:opacity-90 mt-2"
                >
                  Επιστροφή στη σύνδεση
                </button>
              </div>
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); handleForgotSubmit() }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-jewel-text mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-jewel-border rounded-lg focus:ring-2 focus:ring-sky-500 bg-white/80 dark:bg-jewel-surface text-gray-900 dark:text-jewel-text"
                      autoComplete="email"
                    />
                  </div>
                </div>
                {forgotStatus === 'error' && (
                  <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                    Σφάλμα αποστολής. Παρακαλώ δοκιμάστε ξανά.
                  </div>
                )}
                <button
                  type="submit"
                  disabled={forgotStatus === 'loading' || !forgotEmail.trim()}
                  className="w-full bg-sky-600 dark:bg-jewel-teal hover:bg-sky-700 dark:hover:bg-jewel-teal-tint disabled:opacity-50 text-white font-semibold py-2 px-4 rounded-lg transition"
                >
                  {forgotStatus === 'loading' ? 'Αποστολή...' : 'Αποστολή Συνδέσμου'}
                </button>
              </form>
            )}
          </>
        )}

        {view === 'reset' && (
          <>
            <div className="text-center mb-8">
              <div className="bg-sky-100 dark:bg-jewel-teal/30 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-sky-600 dark:text-jewel-teal-tint" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-jewel-text mb-2">Νέος Κωδικός</h1>
              <p className="text-gray-600 dark:text-jewel-text-muted text-sm">Ορίστε τον νέο σας κωδικό πρόσβασης.</p>
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); handleResetSubmit() }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-jewel-text mb-2">Νέος Κωδικός</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
                  <input
                    type="password"
                    value={resetForm.password}
                    onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-jewel-border rounded-lg focus:ring-2 focus:ring-sky-500 bg-white/80 dark:bg-jewel-surface text-gray-900 dark:text-jewel-text"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-jewel-text mb-2">Επιβεβαίωση Κωδικού</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
                  <input
                    type="password"
                    value={resetForm.confirm}
                    onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-jewel-border rounded-lg focus:ring-2 focus:ring-sky-500 bg-white/80 dark:bg-jewel-surface text-gray-900 dark:text-jewel-text"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              {resetError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {resetError}
                </div>
              )}
              <button
                type="submit"
                disabled={resetLoading || !resetForm.password || !resetForm.confirm}
                className="w-full bg-sky-600 dark:bg-jewel-teal hover:bg-sky-700 dark:hover:bg-jewel-teal-tint disabled:opacity-50 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                {resetLoading ? 'Αποθήκευση...' : 'Αποθήκευση Νέου Κωδικού'}
              </button>
            </form>
          </>
        )}

        {view === 'resetDone' && (
          <div className="text-center space-y-5">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-bold text-gray-800 dark:text-jewel-text">Ο κωδικός άλλαξε!</h1>
            <p className="text-gray-600 dark:text-jewel-text-muted">Μπορείτε τώρα να συνδεθείτε με τον νέο σας κωδικό.</p>
            <button
              onClick={() => setView('login')}
              className="w-full bg-calm-green-gradient dark:bg-none dark:bg-jewel-forest text-black dark:text-jewel-text font-semibold py-2 px-4 rounded-lg transition hover:opacity-90"
            >
              Μετάβαση στη Σύνδεση
            </button>
          </div>
        )}

      </div>
    </div>
  )
}

export default LoginScreen